import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";
import useData from "../../hooks/useData";

import { Box, Stack, Button, TextField } from "@mui/material";

import Page from "./Page";
import Card from "../layout/Card";
import Select from "../elements/Select";

const ClientExports = () => {
  const { getTitle } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToWorkQueue, getFromServer } = useData();
  const { showMessage } = usePage();

  const [currentExport, setCurrentExport] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [callStatus, setCallStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);
  const entityName = location.state.entityName;
  const entityId = location.state.entityId;
  const name = location.state.name;

  useEffect(() => {
    const loadProjects = async () => {
      if (!entityId) {
        setProjects([]);
        return;
      }

      const results = await getFromServer({
        entityName: "Project",
        filter: { clientId: entityId },
      });

      const list = Array.isArray(results) ? results : [];
      list.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
      setProjects(list);
    };

    loadProjects();
  }, [entityId, getFromServer]);

  const handleExportSelection = async (selection) => {
    switch (selection) {
      case "skuTemplate":
        await getFile("exportSkuImportTemplate");
        break;

      case "skuList":
        await getFile("exportClientSkus");
        break;

      case "listingsTemplate":
        await getFile("exportListingsTemplate");
        break;

      case "clientListings":
        await getFile("exportClientListings");
        break;

      case "skuPricingTemplate":
        await getFile("exportSkuPricingTemplate");
        break;

      case "calls":
        setCurrentExport("calls");
        break;
    }
  };

  const exportCalls = async () => {
    await addToWorkQueue("exportCalls", {
      entityName,
      entityId,
      email,
      userId: currentUserId,
      filter: {
        clientId: entityId,
        projectId,
        callStatus,
        fromDate,
        toDate,
      },
    });

    showMessage("Your file will be emailed to you shortly.");
  };

  const getFile = async (name) => {
    await addToWorkQueue(name, {
      entityName,
      entityId,
      email,
      userId: currentUserId,
      filter: {
        clientId: entityId,
      },
    });

    showMessage("Your file will be emailed to you shortly.");
  };

  const handleToolbarClick = (action) => {
    switch (action) {
      case "back":
        navigate("/client", { state: { clientId: entityId } });
        break;
    }
  };

  return (
    <Page
      pageName="Client Exports"
      entityName={name}
      includeToolbar={true}
      items={[
        {
          name: "back",
          iconName: "ArrowCircleLeft",
        },
      ]}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        sx={{ display: "flex" }}
        gap={1}
      >
        <Card
          title={getTitle("exports")}
          sx={{ width: "300px" }}
        >
          <Stack>
            <Box>
              <Button onClick={() => handleExportSelection("skuTemplate")}>
                SKU Template
              </Button>
            </Box>
            <Box>
              <Button
                onClick={() => handleExportSelection("skuPricingTemplate")}
              >
                SKU Pricing Template
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("skuList")}>
                Client SKU List
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("listingsTemplate")}>
                SKU Listings Template
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("clientListings")}>
                Client Listings
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("calls")}>
                Calls
              </Button>
            </Box>
          </Stack>
        </Card>

        {currentExport === "calls" && (
          <Card
            title="Calls Export"
            sx={{ width: "360px" }}
          >
            <Stack
              spacing={1}
              padding="10px"
            >
              <Select
                label="project"
                value={projectId}
                onChange={setProjectId}
                items={projects}
                includeNone
                noneLabel="All Projects"
              />

              <Select
                label="callStatus"
                value={callStatus}
                onChange={setCallStatus}
                items={[
                  { id: "future", name: "Future" },
                  { id: "incomplete", name: "Incomplete" },
                  { id: "complete", name: "Complete" },
                  { id: "expired", name: "Expired" },
                ]}
                includeNone
                noneLabel="All Statuses"
              />

              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />

              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />

              <Button
                variant="contained"
                onClick={exportCalls}
              >
                Export Calls
              </Button>
            </Stack>
          </Card>
        )}
      </Box>
    </Page>
  );
};

export default ClientExports;
